<template>
    <v-card>
        <v-card-title class="d-flex flex-column align-start text-subtitle-1 text-sm-h6">
            <span>Rechnung Nr. {{ editedItem.nummer }} bearbeiten</span>
            <div v-if="editedItem && (editedItem.nachname || editedItem.vorname)" class="d-flex align-center">
                <v-icon small left>mdi-account</v-icon>
                <span class="text-body-2">{{ editedItem.nachname }} {{ editedItem.vorname }}</span>
                <v-chip v-for="merkmal in clientMerkmale" :key="'chip-' + merkmal.id" class="ml-2 font-weight-regular" small>{{ merkmal.merkmal }}</v-chip>
            </div>
        </v-card-title>

        <v-card-text class="pt-3">
            <!-- Invoice Details Panel (Always Expanded) -->
            <v-expansion-panels :value="0" readonly class="mt-2 flat-expansion-panels" flat>
                <v-expansion-panel>
                    <v-expansion-panel-header>
                        <div class="d-flex align-center">
                            <v-icon left>mdi-file-document-edit-outline</v-icon>
                            <span class="text-h6">Rechnungsdetails</span>
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-form ref="form" v-model="formValid">
                            <!-- Invoice Date & Number Row -->
                            <v-row class="mt-3">
                                <v-col cols="6" :sm="editedItem.beleg_nummer ? 4 : 6" class="my-0 py-0">
                                    <v-menu v-model="menu" :close-on-content-click="false" :disabled="!!editedItem.beleg_nummer" offset-y max-width="290px" min-width="auto">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field 
                                                dense 
                                                outlined 
                                                v-model="computedDateFormatted"
                                                label="Rechnungsdatum" 
                                                prepend-inner-icon="mdi-calendar" 
                                                readonly 
                                                :disabled="!!editedItem.beleg_nummer"
                                                :hint="editedItem.beleg_nummer ? 'Nicht änderbar da bereits ein Beleg erstellt wurde.' : ''"
                                                :persistent-hint="!!editedItem.beleg_nummer"
                                                v-bind="attrs"
                                                v-on="on" 
                                            />
                                        </template>
                                        <v-date-picker 
                                            first-day-of-week="1" 
                                            v-model="editedItem.datum" 
                                            no-title 
                                            @input="menu = false" 
                                        />
                                    </v-menu>
                                </v-col>
                                <v-col cols="6" :sm="editedItem.beleg_nummer ? 4 : 6" class="my-0 py-0">
                                    <v-text-field 
                                        :disabled="!!editedItem.beleg_nummer" 
                                        dense 
                                        outlined 
                                        v-model="editedItem.nummer" 
                                        label="Rechnungsnummer" 
                                        :hint="hintInvoiceNumber" 
                                        :persistent-hint="!!editedItem.beleg_nummer"
                                        :rules="invoiceNumberRules"
                                    />
                                </v-col>
                                <v-col v-if="!!editedItem.beleg_nummer" cols="6" sm="4" class="my-0 py-0">
                                    <v-text-field 
                                        disabled 
                                        dense 
                                        outlined 
                                        :value="editedItem.beleg_nummer"
                                        label="Belegnummer" 
                                        persistent-hint 
                                        hint="Nicht änderbar." 
                                    />
                                </v-col>
                            </v-row>

                            <!-- Invoice Year Mismatch Alert -->
                            <v-alert
                                v-if="hasInvoiceYearMismatch"
                                dense
                                outlined
                                color="orange"
                                type="error"
                                class="text-justify mt-4"
                            >
                                <span class="font-weight-bold">Achtung:</span>
                                Die Rechnungsnummer enthält das Jahr <strong>{{ invoiceYear }}</strong>, das sich vom Jahr des Rechnungsdatums
                                <strong>{{ formatInvoiceDateYear(editedItem.datum) }}</strong> unterscheidet. Bitte prüfe, ob dies korrekt ist.
                                <p class="mt-3">
                                    Um die Rechnungsnummer auf das Jahr <strong>{{ formatInvoiceDateYear(editedItem.datum) }}</strong> umzustellen, klicke auf <strong>Umstellen</strong>
                                    oder überschreibe die vorgeschlagene Rechnungsnummer.
                                    Sollte die Rechnungsnummer {{ editedItem.nummer }} richtig sein, kannst du diese Warnung ignorieren.
                                </p>
                                <v-btn class="mb-3" :color="$store.state.theme.green" outlined @click="updateInvoiceNumberToCurrentYear">
                                    Umstellen
                                </v-btn>
                            </v-alert>

                            <!-- Warning for changing paid invoice date -->
                            <v-alert
                                v-if="hasInvoiceDateWarning"
                                dense
                                outlined
                                type="warning"
                                prominent
                                class="mt-4"
                            >
                                <span class="font-weight-bold">Diese Rechnung wurde bereits bezahlt.</span><br>
                                Das Ändern des Rechnungsdatums kann Auswirkungen auf die Buchhaltung und Steuerperioden haben.
                            </v-alert>

                            <!-- Warning for changing paid invoice number -->
                            <v-alert
                                v-if="hasInvoiceNumberWarning"
                                dense
                                outlined
                                type="warning"
                                prominent
                                class="mt-4"
                            >
                                <span class="font-weight-bold">Diese Rechnung wurde bereits bezahlt.</span><br>
                                Das Ändern der Rechnungsnummer kann die Nachverfolgung von Zahlungen beeinträchtigen und es können Lücken in der fortlaufenden Nummerierung entstehen.
                            </v-alert>

                            <!-- Payment Method Section -->
                            <div v-if="canEditPaymentMethod" class="mt-6">
                                <p class="text-subtitle-1 font-weight-medium mb-4">
                                    Zahlungsmethode
                                </p>
                                <v-tabs v-model="selectedPaymentMethod" class="mt-3 payment-method-tabs" @change="onPaymentMethodChange" grow hide-slider active-class="payment-tab-active">
                                    <v-tab>              
                                        <v-icon left>mdi-bank-transfer</v-icon>              
                                        Überweisung
                                    </v-tab>
                                    <v-tab>                            
                                        <v-icon left>mdi-cash</v-icon>
                                        Bar
                                    </v-tab>
                                    <v-tab>
                                        <v-icon left>mdi-credit-card</v-icon>
                                        Bankomat
                                    </v-tab>
                                </v-tabs>

                                <!-- Payment Method Info -->
                                <v-alert dense outlined type="info" class="mt-4">
                                    <span v-if="selectedPaymentMethod === 0">Überweisungen werden meist später bezahlt. Du kannst die Rechnung nachträglich als "Bezahlt" markieren.</span>
                                    <span v-else-if="selectedPaymentMethod === 1">Barzahlungen werden normalerweise sofort bezahlt. Das Bezahldatum ist erforderlich.</span>
                                    <span v-else-if="selectedPaymentMethod === 2">Bankomatzahlungen werden normalerweise sofort bezahlt. Das Bezahldatum ist erforderlich.</span>
                                </v-alert>
                            </div>

                            <!-- Payment Method Read-Only Display -->
                            <div v-else class="mt-6">
                                <p class="text-subtitle-1 font-weight-medium mb-4">
                                    Zahlungsmethode
                                </p>
                                <v-chip :color="$store.state.theme.primary" dark class="mb-2">
                                    <v-icon left small>{{ paymentMethodIcon }}</v-icon>
                                    {{ paymentMethodLabel }}
                                </v-chip>
                                <p class="text-caption text--secondary">
                                    {{ paymentMethodReadOnlyHint }}
                                </p>
                            </div>

                            <!-- Payment Details Section -->
                            <div class="mt-6">
                                <p class="text-subtitle-1 font-weight-medium mb-4">
                                    Zahlungsstatus
                                </p>

                                <v-row class="d-flex align-start">
                                    <v-col cols="12" class="d-flex align-start my-0 py-0">
                                        <v-menu v-model="menu_paid" :close-on-content-click="true" :open-on-click="!editedItem.beleg_nummer"
                                            offset-y max-width="290px" min-width="auto">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field 
                                                    dense 
                                                    outlined 
                                                    v-model="paidDateText"
                                                    label="Bezahlt am" 
                                                    prepend-inner-icon="mdi-calendar"
                                                    :clearable="isTransferPayment && !editedItem.beleg_nummer"
                                                    clear-icon="mdi-close-circle"
                                                    @click:clear="clearPaymentDate"
                                                    :rules="paymentDateRules"
                                                    :disabled="!!editedItem.beleg_nummer"
                                                    @input="updateDatePicker"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    :hint="paymentDateHint" 
                                                    :persistent-hint="!!editedItem.beleg_nummer || isImmediatePaymentMethod"
                                                />
                                            </template>
                                            <v-date-picker
                                                full-width 
                                                first-day-of-week="1" 
                                                v-model="editedItem.bezahlt" 
                                                no-title 
                                                :color="$store.state.theme.green"
                                                elevation="1"
                                                @input="updateTextField" 
                                                class="mt-2 py-3"
                                            />
                                        </v-menu>
                                    </v-col>
                                </v-row>

                                <!-- Warning for changing payment date -->
                                <v-alert
                                    v-if="hasPaymentDateWarning"
                                    dense
                                    outlined
                                    type="warning"
                                    prominent
                                    class="mt-4"
                                >
                                    <span class="font-weight-bold">Das Bezahldatum wurde geändert.</span><br>
                                    Nach dem Zufluss-Abfluss Prinzip kann dies Auswirkungen auf die Steuerperiode und Buchhaltung haben.
                                </v-alert>
                            </div>

                            <!-- Receipt Warning Alert -->
                            <v-alert v-if="!!editedItem.beleg_nummer && editedItem.bar === false"
                                outlined type="warning" prominent border="left" class="mt-4">
                                <span class="font-weight-bold">Achtung:</span>
                                Du hast bereits einen Beleg für diese Rechnung erstellt.
                                Wenn du die Barzahlung rückgängig machst und jetzt speicherst, wird der Beleg gelöscht!
                            </v-alert>
                        </v-form>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </v-card-text>

        <v-card-actions class="d-flex justify-end mt-5 px-6 pb-5">
            <v-btn :color="$store.state.theme.primary" text @click="$emit('close')">
                Abbrechen
            </v-btn>
            <v-btn 
                :color="hasAnyWarning ? $store.state.theme.orange : $store.state.theme.green" 
                outlined 
                @click="save" 
                :loading="updating_invoice" 
                :disabled="!formValid"
            >
                {{ hasAnyWarning ? 'Trotzdem Speichern' : 'Speichern' }}
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import dayjs from 'dayjs';
import connector from '../helpers/supabase-connector.js';
import invoiceHelper from '../helpers/invoices.js';

export default {
    props: {
        editedItem: {
            type: Object,
            required: true
        },
        editedIndex: {
            type: [Number, String],
            required: true
        },
        session: {
            type: Object,
            required: true
        }
    },
    emits: ['close', 'initialize', 'showError', 'showInfo'],

    data() {
        return {
            menu: false,
            menu_paid: false,
            updating_invoice: false,
            formValid: true,
            paidDateText: '',
            selectedPaymentMethod: 0, // 0=Transfer, 1=Cash, 2=Card
            originalPaymentDate: null, // Store original payment date to detect changes and determine editability
            originalInvoiceDate: null, // Track original date to detect changes
            originalInvoiceNumber: null, // Track original number to detect changes

            invoiceNumberRules: [
                v => !!v || 'Eine Rechnungsnummer muss vergeben werden.'
            ]
        }
    },

    computed: {
        clientMerkmale() {
            return this.editedItem.merkmale || [];
        },

        computedDateFormatted() {
            return this.formatDate(this.editedItem.datum);
        },

        hintInvoiceNumber() {
            if (this.editedItem.beleg_nummer) {
                return 'Nicht änderbar da bereits ein Beleg erstellt wurde.';
            }
            return '';
        },

        invoiceYear() {
            try {
                if (!this.editedItem.nummer) return null;
                
                const format = invoiceHelper.getInvoiceFormat(this.editedItem.nummer);
                const { year } = invoiceHelper.parseInvoiceNumber(this.editedItem.nummer, format);
                
                return year;
            } catch (error) {
                return null;
            }
        },

        hasInvoiceYearMismatch() {
            if (!this.invoiceYear || !this.editedItem.datum) return false;
            
            const invoiceDateYear = dayjs(this.editedItem.datum).year();
            return this.invoiceYear !== invoiceDateYear;
        },

        canEditPaymentMethod() {
            // Can edit if originally unpaid (prevents tabs from disappearing when user selects payment method during editing)
            return !this.originalPaymentDate;
        },

        isTransferPayment() {
            return this.selectedPaymentMethod === 0;
        },

        isImmediatePaymentMethod() {
            return this.selectedPaymentMethod === 1 || this.selectedPaymentMethod === 2;
        },

        paymentMethodIcon() {
            if (this.editedItem.bankomat) return 'mdi-credit-card';
            if (this.editedItem.bar) return 'mdi-cash';
            return 'mdi-bank-transfer';
        },

        paymentMethodLabel() {
            if (this.editedItem.bankomat) return 'Bankomat';
            if (this.editedItem.bar) return 'Bar';
            return 'Überweisung';
        },

        paymentMethodReadOnlyHint() {
            if (this.editedItem.bezahlt) return 'Zahlungsmethode kann nach Bezahlung nicht mehr geändert werden.';
            return '';
        },

        paymentDateHint() {
            if (this.editedItem.beleg_nummer) return 'Nicht änderbar da bereits ein Beleg erstellt wurde.';
            if (this.isImmediatePaymentMethod) return 'Sofortige Zahlungen erfordern ein Bezahldatum.';
            if (this.isTransferPayment) return 'Überweisungen können später bezahlt werden.';
            return '';
        },

        paymentDateRules() {
            return [
                v => {
                    // If it's an immediate payment method and no receipt exists, payment date is required
                    if (this.isImmediatePaymentMethod && !this.editedItem.beleg_nummer) {
                        return !!v || 'Bezahldatum ist bei sofortigen Zahlungen erforderlich.';
                    }
                    return true;
                },
                v => {
                    if (!v) return true; // Skip format validation if empty
                    return /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[012])\.(\d{4})$/.test(v) || 'Datum im Format TT.MM.JJJJ angeben.';
                },
            ];
        },

        // Warning detection for risky changes to paid invoices
        hasInvoiceDateWarning() {
            return this.originalPaymentDate && 
                   this.originalInvoiceDate && 
                   this.editedItem.datum !== this.originalInvoiceDate;
        },

        hasInvoiceNumberWarning() {
            return this.originalPaymentDate && 
                   this.originalInvoiceNumber && 
                   this.editedItem.nummer !== this.originalInvoiceNumber;
        },

        hasPaymentDateWarning() {
            return this.originalPaymentDate && 
                   this.formatDate(this.editedItem.bezahlt) !== this.formatDate(this.originalPaymentDate);
        },

        hasAnyWarning() {
            return this.hasInvoiceDateWarning || this.hasInvoiceNumberWarning || this.hasPaymentDateWarning;
        }
    },

    watch: {
        'editedItem.bezahlt': {
            immediate: true,
            handler(newVal) {
                this.paidDateText = this.formatDate(newVal);
            }
        }
    },

    mounted() {
        this.initializePaymentMethod();
        // Store original values to determine editability and detect risky changes
        this.originalPaymentDate = this.editedItem.bezahlt;
        this.originalInvoiceDate = this.editedItem.datum;
        this.originalInvoiceNumber = this.editedItem.nummer;
    },

    methods: {
        initializePaymentMethod() {
            // Set selected payment method based on existing invoice data
            if (this.editedItem.bankomat) {
                this.selectedPaymentMethod = 2; // Bankomat
            } else if (this.editedItem.bar) {
                this.selectedPaymentMethod = 1; // Bar
            } else {
                this.selectedPaymentMethod = 0; // Überweisung
            }
        },

        onPaymentMethodChange(tabIndex) {
            if (!this.canEditPaymentMethod) return;

            if (tabIndex === 1) { // Bar (Cash)
                this.setTodaysDate();
                this.editedItem.bar = true;
                this.editedItem.bankomat = false;
            } else if (tabIndex === 2) { // Bankomat (Card)
                this.setTodaysDate();
                this.editedItem.bar = true;
                this.editedItem.bankomat = true;
            } else if (tabIndex === 0) { // Überweisung (Transfer)
                this.clearPaymentDate();
                this.editedItem.bar = false;
                this.editedItem.bankomat = false;
            }
        },

        setTodaysDate() {
            const today = new Date();
            this.editedItem.bezahlt = today.toISOString().substring(0, 10);
            this.paidDateText = this.formatDate(this.editedItem.bezahlt);
        },

        formatDate(date) {
            if (!date) return null;
            const [year, month, day] = date.split('T')[0].split('-');
            return `${day}.${month}.${year}`;
        },

        formatInvoiceDateYear(dateStr) {
            if (!dateStr) return null;
            return dayjs(dateStr).format('YYYY');
        },

        updateInvoiceNumberToCurrentYear() {
            const result = invoiceHelper.updateInvoiceNumberToCurrentYear(
                this.editedItem.nummer, 
                this.editedItem.datum
            );
            
            if (result.status === "success") {
                this.editedItem.nummer = result.invoiceNumber;
            } else {
                this.$emit('showError', { 
                    message: result.message, 
                    error: result.error 
                });
            }
        },

        clearPaymentDate() {
            this.editedItem.bezahlt = null;
            this.paidDateText = '';
        },

        updateTextField(newDate) {
            this.paidDateText = this.formatDate(newDate);
        },

        updateDatePicker() {
            if (this.paidDateText === '') {
                this.clearPaymentDate();
            } else {
                const parsedDate = dayjs(this.paidDateText, 'DD.MM.YYYY', true);
                if (parsedDate.isValid()) {
                    this.editedItem.bezahlt = parsedDate.format('YYYY-MM-DD');
                }
            }
        },

        async save() {
            this.updating_invoice = true;

            // Prepare invoice data for update
            let invoice = JSON.parse(JSON.stringify(this.editedItem));
            
            // Format date properly
            invoice.datum = this.editedItem.datum.split('T')[0];

            // Validate invoice number
            if (invoice.nummer === '' || invoice.nummer === null) {
                this.$emit('showError', { 'message': "Eine Rechnungsnummer muss vergeben werden." });
                this.updating_invoice = false;
                return;
            }

            // Validate payment date for immediate payment methods
            if (this.isImmediatePaymentMethod && !invoice.bezahlt && !invoice.beleg_nummer) {
                this.$emit('showError', { 'message': "Bei sofortigen Zahlungen muss ein Bezahldatum angegeben werden." });
                this.updating_invoice = false;
                return;
            }

            // Validate invoice number format (only for clients, not institutions)
            if (!this.editedItem.fk_institution_id) {
                if (this.$store.state.client.rechnungsnummer_format) {
                    const invoice_regex = invoiceHelper.generateInvoiceRegex(this.$store.state.client.rechnungsnummer_format);
                    if (!invoice_regex.test(invoice.nummer)) {
                        this.$emit('showError', { 'message': "Die Rechnungsnummer muss folgendes Format haben: " + this.$store.state.client.rechnungsnummer_format });
                        this.updating_invoice = false;
                        return;
                    }
                } else if (this.$store.state.client.eigenes_schema) {
                    // do nothing, as we do not have a client schema
                } else {
                    const invoice_regex = invoiceHelper.generateInvoiceRegex('{NR}/{JAHR}');
                    if (!invoice_regex.test(invoice.nummer)) {
                        this.$emit('showError', { 'message': "Die Rechnungsnummer muss folgendes Format haben: NR/JJJJ (zb. 1/2022)." });
                        this.updating_invoice = false;
                        return;
                    }
                }
            }

            // Prepare update object with editable fields
            let invoice_to_insert = { 
                'bezahlt': invoice.bezahlt, 
                'nummer': invoice.nummer, 
                'datum': invoice.datum, 
                'bar': invoice.bar || false, 
                'bankomat': invoice.bankomat || false
            };

            try {
                let updated = await connector.update(this, 'rechnungen', invoice_to_insert, this.editedIndex);
                if (!updated) {
                    this.updating_invoice = false;
                    return;
                }

                this.$emit('initialize');
                this.$emit('close');
                this.updating_invoice = false;
                this.$emit('showInfo', {
                    message: 'Die Rechnung wurde erfolgreich gespeichert.',
                    timeout: 5000
                });

            } catch (error) {
                this.$emit('showError', error);
                this.updating_invoice = false;
            }
        }
    }
}
</script>

<style scoped>
.v-data-table >>> .no-hover:hover {
  background-color: transparent !important;
  cursor: default;
}
.clickable-rows ::v-deep tbody tr {
  cursor: pointer;
}
.flat-expansion-panels ::v-deep .v-expansion-panel {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  margin-bottom: 8px;
}
.flat-expansion-panels ::v-deep .v-expansion-panel:last-child {
  margin-bottom: 0;
}
.payment-method-tabs ::v-deep .v-tabs-bar {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
.payment-method-tabs ::v-deep .v-tab:not(:last-child) {
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}
.payment-method-tabs ::v-deep .payment-tab-active {
  background-color: hsl(210, 15%, 90%);
  color: hsl(210, 15%, 20%);
}
.payment-method-tabs ::v-deep .v-tab:hover:not(.payment-tab-active) {
  background-color: hsl(210, 10%, 95%);
}
</style>